import { useAxios } from '@/context/axiosContext'
import { useEffect, useRef, useState } from 'react'
import { SurveyData, SurveyGroupData } from './surveyItem'
import { SurveyListItem } from './_components/surveyListItem'
import GGOOLLFF from '@/assets/images/logo.png'
import './survey.css'
import MenuInfo from '../menu/menuInfo'
import { MenuHeader } from '../menu/menuHeader'
import { useLocation } from 'react-router-dom'
import { SEO } from '@/components/seo/seo'
import { useResponsive } from '@/hooks/useResponsive'

// 스켈레톤 로딩 컴포넌트
const SurveySkeleton: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  return (
    <div
      className="survey-skeleton-container"
      style={{ margin: isMobile ? '0 16px' : '0' }}
    >
      {[...Array(3)].map((_, index) => (
        <div key={index} className="survey-skeleton-item">
          <div className="survey-skeleton-header">
            <div className="survey-skeleton-title"></div>
            <div className="survey-skeleton-date"></div>
          </div>
          <div className="survey-skeleton-description"></div>
          <div className="survey-skeleton-options">
            {[...Array(2)].map((_, optionIndex) => (
              <div key={optionIndex} className="survey-skeleton-option">
                <div className="survey-skeleton-option-text"></div>
                <div className="survey-skeleton-option-bar"></div>
              </div>
            ))}
          </div>
          <div className="survey-skeleton-footer">
            <div className="survey-skeleton-voters"></div>
            <div className="survey-skeleton-button"></div>
          </div>
        </div>
      ))}
    </div>
  )
}

const Survey: React.FC = () => {
  const axios = useAxios()
  const location = useLocation()
  const menuInfoRef = useRef<HTMLDivElement | null>(null)
  const [surveyList, setSurveyList] = useState<SurveyGroupData[]>([])
  const [surveySn, setSurveySn] = useState<number>()
  const [hasMenuInfo, setHasMenuInfo] = useState<boolean>(true)
  const { isMobile, isTablet, isDesktop } = useResponsive()

  useEffect(() => {
    getSurveyList()
  }, [])

  const getSurveyList = async () => {
    const response = await axios.get(`/survey/getSurveyList`, {
      headers: { 'Cache-Control': 'no-cache' },
    })

    const { surveyList } = response.data.data as {
      surveyList: SurveyData[]
    }

    setSurveySn(
      surveyList.find((survey) => survey.surveyActive === 'Y')?.surveySn,
    )

    if (surveyList && Array.isArray(surveyList)) {
      const groupedSurveys: SurveyGroupData[] = Object.values(
        surveyList.reduce(
          (acc, item) => {
            const {
              cmpnyCd,
              regId,
              regDate,
              updId,
              updDate,
              surveySn,
              surveyTitle,
              surveyDesc,
              surveyStartDate,
              surveyEndDate,
              isAnonymous,
              isMultiple,
              maxChoices,
              optionSn,
              optionTitle,
              optionDesc,
              surveyCount,
              surveyActive,
              totalVoters,
              useYn,
              delYn,
              userOptionSn,
              votePercent,
            } = item

            if (!acc[surveySn]) {
              acc[surveySn] = {
                cmpnyCd,
                regId,
                regDate,
                updId,
                updDate,
                surveySn,
                surveyTitle,
                surveyDesc,
                surveyStartDate,
                surveyEndDate,
                isAnonymous,
                isMultiple,
                maxChoices,
                surveyActive,
                useYn,
                delYn,
                userOptionSn,
                totalVoters,
                votePercent,
                options: [],
              }
            }

            acc[surveySn].options.push({
              surveySn,
              optionSn,
              optionTitle,
              optionDesc,
              surveyCount,
              votePercent,
              userOptionSn,
            })

            return acc
          },
          {} as Record<number, SurveyGroupData>,
        ),
      ).sort((a, b) => {
        // 활성화된 항목 먼저
        if (a.surveyActive === 'Y' && b.surveyActive !== 'Y') return -1
        if (a.surveyActive !== 'Y' && b.surveyActive === 'Y') return 1

        // 시작일 기준 내림차순 (최신이 먼저)
        return (
          new Date(b.surveyStartDate).getTime() -
          new Date(a.surveyStartDate).getTime()
        )
      })

      setSurveyList(groupedSurveys)
    }
  }

  return (
    <>
      <SEO
        title="투표"
        //description="꿀프에서 골프 관련 서비스를 확인하세요."
        canonicalUrl="https://ggoollff.co.kr/survey"
      />
      <div style={{ padding: isMobile ? '0 16px' : '0' }}>
        <MenuHeader
          pathName={location.pathname}
          showWriteButton={false}
          menuInfoRef={menuInfoRef}
          loginRequired={true}
          iconActive={hasMenuInfo}
        />
      </div>
      {surveyList.length > 0 ? (
        <SurveyListItem
          surveyList={surveyList}
          activeSurveySn={surveySn}
          onRefresh={() => getSurveyList()}
        />
      ) : (
        <div
          className="no-data-message"
          style={{ margin: isMobile ? '0 15px' : '0', width: 'auto' }}
        >
          <img src={GGOOLLFF} alt="noData" width={'150px'} />
          <div>진행중인 투표가 없습니다.</div>
        </div>
      )}
      <div ref={menuInfoRef}>
        <MenuInfo menuPath={location.pathname} onDataCheck={setHasMenuInfo} />
      </div>
    </>
  )
}

export default Survey
